import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, where, doc, updateDoc, addDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { softDelete } from '../services/firestoreService';
import Breadcrumb from '../components/ui/Breadcrumb';
import { getReportTypeLabel, getStatusColor, formatDate, getTotalVotes } from '../utils/labelHelpers';
import '../styles/AdminActionReports.css';

const DISTRICT_ROLES = ['dCOM', 'dCOM Chair', 'dCOM Member', 'DS'];

const AdminActionReports = () => {
  const { currentUser, districts } = useAuth();
  const toast = useToast();
  const [actionReports, setActionReports] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  const isDCOM = DISTRICT_ROLES.includes(currentUser?.role);
  const userDistrictIds = currentUser?.districtIds || (currentUser?.districtId ? [currentUser.districtId] : []);
  const userDistrictNames = isDCOM && districts.length > 0
    ? userDistrictIds.map(id => districts.find(d => d.id === id)?.name).filter(Boolean)
    : [];
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [filteredReports, setFilteredReports] = useState([]);
  const [editingReport, setEditingReport] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedReports, setSelectedReports] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterReports();
  }, [actionReports, searchTerm, statusFilter, typeFilter]);

  const fetchData = async () => {
    try {
      const [reportsSnapshot, candidatesSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'actionReports'), orderBy('date', 'desc'))),
        getDocs(collection(db, 'candidates')),
      ]);

      const candidatesList = candidatesSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      let reportsList = reportsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      // District-scoped users see only reports for candidates in their district(s)
      if (isDCOM && userDistrictNames.length > 0) {
        const districtCandidateIds = new Set(
          candidatesList
            .filter(c => userDistrictNames.includes(c.district))
            .map(c => c.id)
        );
        reportsList = reportsList.filter(r => districtCandidateIds.has(r.candidateId));
        // Also scope the candidate dropdown to district candidates only
        setCandidates(candidatesList.filter(c => userDistrictNames.includes(c.district)));
      } else {
        setCandidates(candidatesList);
      }

      setActionReports(reportsList);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterReports = () => {
    let filtered = actionReports;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(report =>
        report.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.candidateName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.committee?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.interviewTeam?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(report => report.status === statusFilter);
    }

    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(report => report.type === typeFilter);
    }

    setFilteredReports(filtered);
  };

  const handleEditReport = (report) => {
    setEditingReport({
      ...report,
      date: report.date ? new Date(report.date).toISOString().split('T')[0] : ''
    });
  };

  const handleUpdateReport = async () => {
    if (!editingReport.title || !editingReport.date) {
      toast.warning('Please fill in required fields (Title and Date)');
      return;
    }

    try {
      const reportRef = doc(db, 'actionReports', editingReport.id);
      const updateData = { ...editingReport, updatedAt: new Date() };
      delete updateData.id;

      await updateDoc(reportRef, updateData);
      await fetchData();
      setEditingReport(null);
      toast.success('Action report updated.');
    } catch (error) {
      console.error('Error updating action report:', error);
      toast.error('Error updating action report. Please try again.');
    }
  };

  const handleAddReport = async (newReport) => {
    if (!newReport.title || !newReport.date || !newReport.candidateId) {
      toast.warning('Please fill in required fields (Title, Date, and Candidate)');
      return;
    }

    try {
      const candidate = candidates.find(c => c.id === newReport.candidateId);
      const reportData = {
        ...newReport,
        candidateName: candidate ? `${candidate.firstName} ${candidate.lastName}` : 'Unknown',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await addDoc(collection(db, 'actionReports'), reportData);
      await fetchData();
      setShowAddModal(false);
      toast.success('Action report added.');
    } catch (error) {
      console.error('Error adding action report:', error);
      toast.error('Error adding action report. Please try again.');
    }
  };

  const handleDeleteReports = async () => {
    try {
      await Promise.all(
        selectedReports.map(reportId => softDelete('actionReports', reportId, currentUser))
      );
      await fetchData();
      setSelectedReports([]);
      setShowDeleteConfirm(false);
      toast.success(`Deleted ${selectedReports.length} report${selectedReports.length !== 1 ? 's' : ''}.`);
    } catch (error) {
      console.error('Error deleting action reports:', error);
      toast.error('Error deleting action reports. Please try again.');
    }
  };

  const handleSelectReport = (reportId) => {
    setSelectedReports(prev => 
      prev.includes(reportId) 
        ? prev.filter(id => id !== reportId)
        : [...prev, reportId]
    );
  };

  const handleSelectAll = () => {
    if (selectedReports.length === filteredReports.length) {
      setSelectedReports([]);
    } else {
      setSelectedReports(filteredReports.map(report => report.id));
    }
  };

  const getStatusStats = () => {
    const stats = {
      pending: 0,
      approved: 0,
      denied: 0,
      deferred: 0,
      completed: 0
    };

    actionReports.forEach(report => {
      if (stats.hasOwnProperty(report.status)) {
        stats[report.status]++;
      }
    });

    return stats;
  };

  if (loading) {
    return (
      <div className="admin-action-reports-container">
        <div className="loading-spinner">Loading action reports...</div>
      </div>
    );
  }

  const statusStats = getStatusStats();

  return (
    <div className="admin-action-reports-container">
      <Breadcrumb items={[
        { label: 'Dashboard', to: '/dashboard' },
        { label: 'Action Reports Administration' },
      ]} />
      <div className="page-header">
        <div className="header-content">
          <h1>Action Reports Administration</h1>
          <p>Manage committee decisions and voting records</p>
        </div>
        <div className="header-actions">
          <button 
            className="add-report-btn"
            onClick={() => setShowAddModal(true)}
          >
            + Add New Report
          </button>
          {selectedReports.length > 0 && (
            <button 
              className="delete-selected-btn"
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete Selected ({selectedReports.length})
            </button>
          )}
        </div>
      </div>

      <div className="stats-overview">
        <div className="stat-card">
          <h3>Total Reports</h3>
          <div className="stat-number">{actionReports.length}</div>
        </div>
        <div className="stat-card pending">
          <h3>Pending</h3>
          <div className="stat-number">{statusStats.pending}</div>
        </div>
        <div className="stat-card approved">
          <h3>Approved</h3>
          <div className="stat-number">{statusStats.approved}</div>
        </div>
        <div className="stat-card denied">
          <h3>Denied</h3>
          <div className="stat-number">{statusStats.denied}</div>
        </div>
        <div className="stat-card deferred">
          <h3>Deferred</h3>
          <div className="stat-number">{statusStats.deferred}</div>
        </div>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search reports, candidates, committees, or interview teams..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-controls">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
            <option value="deferred">Deferred</option>
            <option value="completed">Completed</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="dcom-review">DCOM Review</option>
            <option value="bom-review">BOM Review</option>
            <option value="candidacy-interview">Candidacy Interview</option>
            <option value="provisional-review">Provisional Review</option>
            <option value="ordination-review">Ordination Review</option>
            <option value="annual-review">Annual Review</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      <div className="reports-table-container">
        <table className="reports-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedReports.length === filteredReports.length && filteredReports.length > 0}
                  onChange={handleSelectAll}
                />
              </th>
              <th>Title</th>
              <th>Candidate</th>
              <th>Type</th>
              <th>Date</th>
              <th>Status</th>
              <th>Vote</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredReports.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-reports">
                  No action reports found matching your criteria.
                </td>
              </tr>
            ) : (
              filteredReports.map(report => (
                <tr key={report.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedReports.includes(report.id)}
                      onChange={() => handleSelectReport(report.id)}
                    />
                  </td>
                  <td>
                    <div className="report-title-cell">
                      <strong>{report.title}</strong>
                      {report.committee && (
                        <div className="committee-name">{report.committee}</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <Link 
                      to={`/candidates/${report.candidateId}`}
                      className="candidate-link"
                    >
                      {report.candidateName}
                    </Link>
                  </td>
                  <td>
                    <span className="report-type-badge">
                      {getReportTypeLabel(report.type)}
                    </span>
                  </td>
                  <td>{formatDate(report.date)}</td>
                  <td>
                    <span className={`status-badge ${getStatusColor(report.status)}`}>
                      {report.status?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </td>
                  <td>
                    {getTotalVotes(report.vote) > 0 ? (
                      <div className="vote-summary-compact">
                        <span className="vote-yes">{report.vote?.yes || 0}Y</span>
                        <span className="vote-no">{report.vote?.no || 0}N</span>
                        <span className="vote-abstain">{report.vote?.abstain || 0}A</span>
                      </div>
                    ) : (
                      <span className="no-vote">No vote</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="edit-btn"
                        onClick={() => handleEditReport(report)}
                        title="Edit Report"
                      >
                        ✏️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editingReport && (
        <EditReportModal 
          report={editingReport}
          setReport={setEditingReport}
          candidates={candidates}
          onSave={handleUpdateReport}
          onCancel={() => setEditingReport(null)}
        />
      )}

      {showAddModal && (
        <AddReportModal 
          candidates={candidates}
          onSave={handleAddReport}
          onCancel={() => setShowAddModal(false)}
        />
      )}

      {showDeleteConfirm && (
        <DeleteConfirmModal
          count={selectedReports.length}
          onConfirm={handleDeleteReports}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}

      <div className="reports-summary">
        <p>Showing {filteredReports.length} of {actionReports.length} action reports</p>
      </div>
    </div>
  );
};

const EditReportModal = ({ report, setReport, candidates, onSave, onCancel }) => {
  return (
    <div className="modal-overlay">
      <div className="modal-content large">
        <h3>Edit Action Report</h3>
        <div className="report-form">
          <div className="form-row">
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={report.title || ''}
                onChange={(e) => setReport({...report, title: e.target.value})}
                placeholder="e.g., DCOM Annual Review 2024"
                required
              />
            </div>
            <div className="form-group">
              <label>Date *</label>
              <input
                type="date"
                value={report.date || ''}
                onChange={(e) => setReport({...report, date: e.target.value})}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Candidate *</label>
              <select
                value={report.candidateId || ''}
                onChange={(e) => {
                  const candidate = candidates.find(c => c.id === e.target.value);
                  setReport({
                    ...report, 
                    candidateId: e.target.value,
                    candidateName: candidate ? `${candidate.firstName} ${candidate.lastName}` : ''
                  });
                }}
                required
              >
                <option value="">Select a candidate</option>
                {candidates.map(candidate => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.firstName} {candidate.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Type</label>
              <select
                value={report.type || ''}
                onChange={(e) => setReport({...report, type: e.target.value})}
              >
                <option value="">Select type</option>
                <option value="dcom-review">DCOM Review</option>
                <option value="bom-review">BOM Review</option>
                <option value="candidacy-interview">Candidacy Interview</option>
                <option value="provisional-review">Provisional Review</option>
                <option value="ordination-review">Ordination Review</option>
                <option value="annual-review">Annual Review</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Committee</label>
              <input
                type="text"
                value={report.committee || ''}
                onChange={(e) => setReport({...report, committee: e.target.value})}
                placeholder="e.g., District Committee on Ordained Ministry"
              />
            </div>
            <div className="form-group">
              <label>Interview Team</label>
              <input
                type="text"
                value={report.interviewTeam || ''}
                onChange={(e) => setReport({...report, interviewTeam: e.target.value})}
                placeholder="e.g., Rev. Smith, Rev. Johnson, Dr. Williams"
              />
            </div>
          </div>

          <div className="vote-section">
            <label>Vote Count</label>
            <div className="vote-inputs">
              <div className="vote-input">
                <label>Yes</label>
                <input
                  type="number"
                  min="0"
                  value={report.vote?.yes || 0}
                  onChange={(e) => setReport({
                    ...report, 
                    vote: {...(report.vote || {}), yes: parseInt(e.target.value) || 0}
                  })}
                />
              </div>
              <div className="vote-input">
                <label>No</label>
                <input
                  type="number"
                  min="0"
                  value={report.vote?.no || 0}
                  onChange={(e) => setReport({
                    ...report, 
                    vote: {...(report.vote || {}), no: parseInt(e.target.value) || 0}
                  })}
                />
              </div>
              <div className="vote-input">
                <label>Abstain</label>
                <input
                  type="number"
                  min="0"
                  value={report.vote?.abstain || 0}
                  onChange={(e) => setReport({
                    ...report, 
                    vote: {...(report.vote || {}), abstain: parseInt(e.target.value) || 0}
                  })}
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Recommendation</label>
            <select
              value={report.recommendation || ''}
              onChange={(e) => setReport({...report, recommendation: e.target.value})}
            >
              <option value="">Select recommendation</option>
              <option value="approve">Approve</option>
              <option value="deny">Deny</option>
              <option value="defer">Defer</option>
              <option value="conditional-approval">Conditional Approval</option>
              <option value="recommend-first-licensing">Recommend First Licensing</option>
              <option value="recommend-continued-licensing">Recommend Continued Licensing</option>
            </select>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={report.notes || ''}
              onChange={(e) => setReport({...report, notes: e.target.value})}
              rows="4"
              placeholder="Meeting notes, discussion points, concerns..."
            />
          </div>
          
          <div className="form-group">
            <label>Next Steps</label>
            <textarea
              value={report.nextSteps || ''}
              onChange={(e) => setReport({...report, nextSteps: e.target.value})}
              rows="3"
              placeholder="Required actions, follow-up items..."
            />
          </div>
          
          <div className="form-group">
            <label>Status</label>
            <select
              value={report.status || 'pending'}
              onChange={(e) => setReport({...report, status: e.target.value})}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="denied">Denied</option>
              <option value="deferred">Deferred</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
        <div className="modal-actions">
          <button onClick={onCancel} className="cancel-btn">
            Cancel
          </button>
          <button onClick={onSave} className="save-btn">
            Update Report
          </button>
        </div>
      </div>
    </div>
  );
};

const AddReportModal = ({ candidates, onSave, onCancel }) => {
  const [newReport, setNewReport] = useState({
    title: '',
    date: '',
    candidateId: '',
    candidateName: '',
    type: '',
    committee: '',
    interviewTeam: '',
    vote: { yes: 0, no: 0, abstain: 0 },
    recommendation: '',
    notes: '',
    nextSteps: '',
    status: 'pending'
  });

  const handleSave = () => {
    onSave(newReport);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content large">
        <h3>Add New Action Report</h3>
        <div className="report-form">
          <div className="form-row">
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={newReport.title}
                onChange={(e) => setNewReport({...newReport, title: e.target.value})}
                placeholder="e.g., DCOM Annual Review 2024"
                required
              />
            </div>
            <div className="form-group">
              <label>Date *</label>
              <input
                type="date"
                value={newReport.date}
                onChange={(e) => setNewReport({...newReport, date: e.target.value})}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Candidate *</label>
              <select
                value={newReport.candidateId}
                onChange={(e) => {
                  const candidate = candidates.find(c => c.id === e.target.value);
                  setNewReport({
                    ...newReport, 
                    candidateId: e.target.value,
                    candidateName: candidate ? `${candidate.firstName} ${candidate.lastName}` : ''
                  });
                }}
                required
              >
                <option value="">Select a candidate</option>
                {candidates.map(candidate => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.firstName} {candidate.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Type</label>
              <select
                value={newReport.type}
                onChange={(e) => setNewReport({...newReport, type: e.target.value})}
              >
                <option value="">Select type</option>
                <option value="dcom-review">DCOM Review</option>
                <option value="bom-review">BOM Review</option>
                <option value="candidacy-interview">Candidacy Interview</option>
                <option value="provisional-review">Provisional Review</option>
                <option value="ordination-review">Ordination Review</option>
                <option value="annual-review">Annual Review</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Committee</label>
              <input
                type="text"
                value={newReport.committee}
                onChange={(e) => setNewReport({...newReport, committee: e.target.value})}
                placeholder="e.g., District Committee on Ordained Ministry"
              />
            </div>
            <div className="form-group">
              <label>Interview Team</label>
              <input
                type="text"
                value={newReport.interviewTeam}
                onChange={(e) => setNewReport({...newReport, interviewTeam: e.target.value})}
                placeholder="e.g., Rev. Smith, Rev. Johnson, Dr. Williams"
              />
            </div>
          </div>

          <div className="vote-section">
            <label>Vote Count</label>
            <div className="vote-inputs">
              <div className="vote-input">
                <label>Yes</label>
                <input
                  type="number"
                  min="0"
                  value={newReport.vote.yes}
                  onChange={(e) => setNewReport({
                    ...newReport, 
                    vote: {...newReport.vote, yes: parseInt(e.target.value) || 0}
                  })}
                />
              </div>
              <div className="vote-input">
                <label>No</label>
                <input
                  type="number"
                  min="0"
                  value={newReport.vote.no}
                  onChange={(e) => setNewReport({
                    ...newReport, 
                    vote: {...newReport.vote, no: parseInt(e.target.value) || 0}
                  })}
                />
              </div>
              <div className="vote-input">
                <label>Abstain</label>
                <input
                  type="number"
                  min="0"
                  value={newReport.vote.abstain}
                  onChange={(e) => setNewReport({
                    ...newReport, 
                    vote: {...newReport.vote, abstain: parseInt(e.target.value) || 0}
                  })}
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Recommendation</label>
            <select
              value={newReport.recommendation}
              onChange={(e) => setNewReport({...newReport, recommendation: e.target.value})}
            >
              <option value="">Select recommendation</option>
              <option value="approve">Approve</option>
              <option value="deny">Deny</option>
              <option value="defer">Defer</option>
              <option value="conditional-approval">Conditional Approval</option>
              <option value="recommend-first-licensing">Recommend First Licensing</option>
              <option value="recommend-continued-licensing">Recommend Continued Licensing</option>
            </select>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={newReport.notes}
              onChange={(e) => setNewReport({...newReport, notes: e.target.value})}
              rows="4"
              placeholder="Meeting notes, discussion points, concerns..."
            />
          </div>
          
          <div className="form-group">
            <label>Next Steps</label>
            <textarea
              value={newReport.nextSteps}
              onChange={(e) => setNewReport({...newReport, nextSteps: e.target.value})}
              rows="3"
              placeholder="Required actions, follow-up items..."
            />
          </div>
          
          <div className="form-group">
            <label>Status</label>
            <select
              value={newReport.status}
              onChange={(e) => setNewReport({...newReport, status: e.target.value})}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="denied">Denied</option>
              <option value="deferred">Deferred</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
        <div className="modal-actions">
          <button onClick={onCancel} className="cancel-btn">
            Cancel
          </button>
          <button onClick={handleSave} className="save-btn">
            Add Report
          </button>
        </div>
      </div>
    </div>
  );
};

const DeleteConfirmModal = ({ count, onConfirm, onCancel }) => {
  return (
    <div className="modal-overlay">
      <div className="modal-content small">
        <h3>Confirm Deletion</h3>
        <p>
          Are you sure you want to delete {count} action report{count > 1 ? 's' : ''}? 
          This action cannot be undone.
        </p>
        <div className="modal-actions">
          <button onClick={onCancel} className="cancel-btn">
            Cancel
          </button>
          <button onClick={onConfirm} className="delete-btn">
            Delete {count} Report{count > 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminActionReports;
